import { Box, useMediaQuery } from "@mui/material";
import {
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

export type StepDirection = "forward" | "backward";

const EXIT_DURATION_MS = 150;
const ENTER_DURATION_MS = 200;
const HEIGHT_DURATION_MS = 220;
const SLIDE_DISTANCE_PX = 12;

type Phase = "idle" | "exiting" | "entering";

type Props<TStep extends string> = {
  stepKey: TStep;
  direction: StepDirection;
  /** Takes the step being displayed rather than plain children, so the outgoing
      step keeps rendering its own content while it fades out. */
  renderStep: (step: TStep) => ReactNode;
};

const getOffset = (phase: Phase, direction: StepDirection) => {
  if (phase === "idle") {
    return 0;
  }

  const isLeaving = phase === "exiting";
  const isForward = direction === "forward";

  // Leaving content moves against the travel direction and arriving content comes
  // in from the other side, so the two read as one push the same way.
  return isForward === isLeaving ? -SLIDE_DISTANCE_PX : SLIDE_DISTANCE_PX;
};

const getTransition = (phase: Phase) => {
  // "entering" is the pre-frame that positions the new content off to the side;
  // it must snap there, otherwise the browser animates the offset swap instead of
  // the arrival that follows it.
  if (phase === "entering") {
    return "none";
  }

  const duration = phase === "exiting" ? EXIT_DURATION_MS : ENTER_DURATION_MS;

  return `opacity ${duration}ms ease, transform ${duration}ms ease`;
};

/**
 * Animates between wizard steps without an animation library: fade the current step out,
 * swap it while invisible, fade the new one in. Sequential rather than crossfaded, so the
 * two never overlap and neither needs absolute positioning. The measured content height
 * is mirrored onto the outer box so the container doesn't jump at the swap.
 */
export const StepTransition = <TStep extends string>({
  stepKey,
  direction,
  renderStep,
}: Props<TStep>) => {
  const [displayedStep, setDisplayedStep] = useState<TStep>(stepKey);
  const [phase, setPhase] = useState<Phase>("idle");
  const [contentHeight, setContentHeight] = useState<number | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const isReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");

  const isTransitioning = displayedStep !== stepKey;

  useEffect(() => {
    if (!isTransitioning) {
      return;
    }

    if (isReducedMotion) {
      setDisplayedStep(stepKey);
      setPhase("idle");

      return;
    }

    setPhase("exiting");

    const timeout = setTimeout(() => {
      setDisplayedStep(stepKey);
      setPhase("entering");
    }, EXIT_DURATION_MS);

    return () => clearTimeout(timeout);
  }, [isTransitioning, isReducedMotion, stepKey]);

  // A step change that reverses before the exit finishes never reaches the swap, so the
  // exiting styles have to be released here — otherwise the content stays at opacity 0
  // for good. Kept in its own effect so the swap effect above keeps its original deps.
  useEffect(() => {
    if (!isTransitioning && phase === "exiting") {
      setPhase("idle");
    }
  }, [isTransitioning, phase]);

  // Flip to "idle" on the frame after the swap so there is a style to move away from.
  useEffect(() => {
    if (phase !== "entering") {
      return;
    }

    const frame = requestAnimationFrame(() => setPhase("idle"));

    return () => cancelAnimationFrame(frame);
  }, [phase]);

  useLayoutEffect(() => {
    const content = contentRef.current;

    if (!content || isReducedMotion) {
      return;
    }

    const observer = new ResizeObserver(() =>
      setContentHeight(content.offsetHeight),
    );

    observer.observe(content);

    return () => observer.disconnect();
  }, [isReducedMotion]);

  if (isReducedMotion) {
    return <Box>{renderStep(displayedStep)}</Box>;
  }

  return (
    <Box
      height={contentHeight ?? undefined}
      overflow="hidden"
      sx={{ transition: `height ${HEIGHT_DURATION_MS}ms ease` }}
    >
      <Box
        ref={contentRef}
        sx={{
          opacity: phase === "idle" ? 1 : 0,
          transform: `translateX(${getOffset(phase, direction)}px)`,
          transition: getTransition(phase),
        }}
      >
        {renderStep(displayedStep)}
      </Box>
    </Box>
  );
};
